import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import axios from "axios";
import { toast } from "sonner";
import {
  Gift,
  Copy,
  Linkedin,
  Twitter,
  Mail,
  Sparkles,
  CheckCircle2,
  ArrowLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralsPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/referrals/me`, { withCredentials: true });
      setData(d);
    } catch (e) {
      toast.error("Failed to load referral info");
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (loading || !user || !data) return null;

  const shareUrl = `${window.location.origin}/?ref=${data.referral_code}`;
  const shareText = `I'm forming my Texas LLC with this step-by-step guide. Use my link and get $${data.discount_for_friend} off:`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent("Texas LLC Formation Guide")}&summary=${encodeURIComponent(shareText)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText + " " + shareUrl)}`;
  const mailUrl = `mailto:?subject=${encodeURIComponent("$5 off the Texas LLC Formation Guide")}&body=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`;

  return (
    <div className="flex min-h-screen flex-col bg-[var(--c-bg)]">
      <Navbar />
      <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6">
        <button
          data-testid="referrals-back"
          onClick={() => navigate("/quickstart")}
          className="mb-6 inline-flex items-center gap-2 text-sm font-bold text-[var(--c-muted)] hover:text-[var(--c-text)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="mb-8">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#A3E6D5]/20 px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-[var(--c-text)]">
            <Sparkles className="h-3 w-3 text-[#1E3A8A]" />
            Refer & Earn
          </div>
          <h1 className="font-heading text-3xl font-black text-[var(--c-text)] sm:text-4xl">
            Give $5. Get $5.
          </h1>
          <p className="mt-2 text-[var(--c-muted)]">
            Share your link with a friend forming an LLC. They get <strong>${data.discount_for_friend} off</strong> the $19 unlock. You get <strong>${data.reward_per_paid_referral} credit</strong> toward our Done-for-you services.
          </p>
        </div>

        {/* Stats row */}
        <div data-testid="referrals-stats" className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-4 shadow-[3px_3px_0px_var(--c-shadow)]">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--c-muted)]">Sign-ups</p>
            <p data-testid="stat-signups" className="font-heading text-2xl font-black text-[var(--c-text)]">{data.referred_signups}</p>
          </div>
          <div className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-4 shadow-[3px_3px_0px_var(--c-shadow)]">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--c-muted)]">Paid referrals</p>
            <p data-testid="stat-paid" className="font-heading text-2xl font-black text-[var(--c-text)]">{data.paid_referrals}</p>
          </div>
          <div className="rounded-xl border-2 border-[#A3E6D5] bg-[#A3E6D5]/15 p-4 shadow-[3px_3px_0px_var(--c-shadow)]">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--c-muted)]">Your credit</p>
            <p data-testid="stat-credit" className="font-heading text-2xl font-black text-[var(--c-text)]">${data.credit_balance.toFixed(2)}</p>
          </div>
        </div>

        {/* Share link card */}
        <div className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-6 shadow-[4px_4px_0px_var(--c-shadow)]">
          <div className="mb-2 flex items-center gap-2">
            <Gift className="h-4 w-4 text-[#1E3A8A]" />
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--c-muted)]">Your referral link</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              data-testid="referral-link-input"
              type="text"
              readOnly
              value={shareUrl}
              onClick={(e) => e.currentTarget.select()}
              className="flex-1 cursor-text rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-3 font-mono text-sm text-[var(--c-text)] focus:outline-none"
            />
            <button
              data-testid="copy-referral-link"
              onClick={copyLink}
              className="flex items-center justify-center gap-2 rounded-lg border-2 border-[var(--c-border)] bg-[#1E3A8A] px-5 py-3 font-bold text-white shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
            >
              {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              data-testid="share-linkedin"
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-full border-2 border-[#0A66C2]/40 bg-[#0A66C2]/5 px-4 py-2 text-sm font-bold text-[#0A66C2] transition-all hover:-translate-y-0.5 hover:bg-[#0A66C2]/10 hover:shadow-[2px_2px_0px_var(--c-shadow)]"
            >
              <Linkedin className="h-3.5 w-3.5" />
              LinkedIn
            </a>
            <a
              data-testid="share-twitter"
              href={twitterUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-2 text-sm font-bold text-[var(--c-text)] transition-all hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_var(--c-shadow)]"
            >
              <Twitter className="h-3.5 w-3.5" />
              X / Twitter
            </a>
            <a
              data-testid="share-mail"
              href={mailUrl}
              className="flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-2 text-sm font-bold text-[var(--c-text)] transition-all hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_var(--c-shadow)]"
            >
              <Mail className="h-3.5 w-3.5" />
              Email
            </a>
          </div>
        </div>

        {/* How it works */}
        <div className="mt-6 rounded-xl border-2 border-dashed border-[var(--c-border-medium)] bg-[var(--c-bg)] p-5">
          <p className="mb-2 font-heading text-sm font-bold uppercase tracking-[0.15em] text-[var(--c-muted)]">How it works</p>
          <ol className="space-y-2 text-sm text-[var(--c-text)]">
            <li><strong>1.</strong> Share your unique link with friends starting an LLC in Texas.</li>
            <li><strong>2.</strong> They get <strong>${data.discount_for_friend} off</strong> automatically applied when they unlock the $19 premium guide.</li>
            <li><strong>3.</strong> Once they pay, you earn <strong>${data.reward_per_paid_referral} credit</strong> instantly. Use it on Registered Agent ($75) or Operating Agreement ($50) services.</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
